import Cookies from "universal-cookie";

/**
 * Single source of truth for cookie consent.
 *
 * Everything that needs to know whether it may run (Matomo, YouTube embeds)
 * asks this module instead of reading cookies itself, and subscribes via
 * `onConsentChange` so that withdrawing consent takes effect immediately
 * without a page reload.
 */

const COOKIE_NAME = "psw_consent";
// The old all-or-nothing cookie. It was set against a notice that offered no
// categories and no way to decline, so it is not treated as a valid decision:
// those visitors are asked again and the cookie is cleaned up on the way.
const LEGACY_COOKIE_NAME = "pswCookieNotice";
const CONSENT_VERSION = 1;
const LIFETIME_IN_DAYS = 180;

// Categories the user can decide about. "necessary" is deliberately absent:
// it is always on, so it is never stored and never asked about.
export const CATEGORIES = ["statistics", "marketing"];

const cookies = new Cookies();
const listeners = [];

function isPlainObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// Reduces any stored value down to a known-good shape, or null if the cookie
// is missing, malformed, or was written by an older version of this schema.
function normalize(value) {
  if (!isPlainObject(value) || value.v !== CONSENT_VERSION) {
    return null;
  }

  const result = {};

  CATEGORIES.forEach(category => {
    result[category] = value[category] === true;
  });

  return result;
}

function read() {
  try {
    return normalize(cookies.get(COOKIE_NAME));
  } catch (error) {
    return null;
  }
}

let currentConsent = read();

function copyOfCurrentConsent() {
  if (currentConsent === null) {
    return null;
  }

  const copy = {};

  CATEGORIES.forEach(category => {
    copy[category] = currentConsent[category];
  });

  return copy;
}

/**
 * @returns {object|null} the stored decision, or null if the visitor has not
 * decided yet. Callers use null to know the banner still needs showing.
 */
export function getConsent() {
  return copyOfCurrentConsent();
}

/**
 * @returns {boolean} whether the visitor has made any decision at all.
 */
export function hasDecision() {
  return currentConsent !== null;
}

/**
 * @param {string} category one of CATEGORIES
 * @returns {boolean}
 */
export function hasConsent(category) {
  return Boolean(currentConsent && currentConsent[category] === true);
}

function expiryDate() {
  const hoursPerDay = 24;
  const minutesPerHour = 60;
  const secondsPerMinute = 60;
  const millisecondsPerSecond = 1000;
  const lifetimeInMilliseconds =
    LIFETIME_IN_DAYS *
    hoursPerDay *
    minutesPerHour *
    secondsPerMinute *
    millisecondsPerSecond;
  const date = new Date();

  date.setTime(date.getTime() + lifetimeInMilliseconds);

  return date;
}

function notifyListeners() {
  listeners.forEach(listener => {
    try {
      listener(copyOfCurrentConsent());
    } catch (error) {
      // One broken subscriber must not stop the others from being told.
      // eslint-disable-next-line no-console
      console.error("consent listener failed", error);
    }
  });
}

/**
 * Stores a decision and tells every subscriber.
 *
 * @param {object} selection e.g. { statistics: true, marketing: false }
 */
export function setConsent(selection) {
  const value = {
    v: CONSENT_VERSION,
    ts: Math.floor(new Date().getTime() / 1000)
  };

  CATEGORIES.forEach(category => {
    value[category] = selection[category] === true;
  });

  cookies.set(COOKIE_NAME, value, {
    path: "/",
    expires: expiryDate(),
    sameSite: "lax",
    // Setting `secure` over plain http would silently drop the cookie, which
    // would break local development.
    secure: window.location.protocol === "https:"
  });

  cookies.remove(LEGACY_COOKIE_NAME, { path: "/" });

  currentConsent = normalize(value);
  notifyListeners();
}

/**
 * Forgets the decision entirely, so the banner is shown again.
 */
export function clearConsent() {
  cookies.remove(COOKIE_NAME, { path: "/" });
  cookies.remove(LEGACY_COOKIE_NAME, { path: "/" });

  currentConsent = null;
  notifyListeners();
}

/**
 * @param {Function} callback called with the new consent object (or null)
 * @returns {Function} unsubscribe
 */
export function onConsentChange(callback) {
  listeners.push(callback);

  return function unsubscribe() {
    const index = listeners.indexOf(callback);

    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
}
